// ── El enlace de pago de los correos (2026-09-15) ────────────────────────────
// Los tres correos de la demo —bienvenida, por vencer, vencida— llevan un botón
// para pagar. Ese botón NO puede apuntar a una URL de Checkout de Stripe: esas
// caducan a las 24 horas y se crean contra una cuenta concreta, así que una
// pegada en un correo o llega vencida o cobra a quien no es.
//
// Apunta acá. Esta pantalla pide sesión (como toda la app), y al abrir dispara
// el checkout sola. Desde el correo es un clic: el cliente no tiene que entrar
// a Ajustes a buscar un botón que no sabe dónde está.
//
// Si hay más de un plan activo no elige por su cuenta — manda a la pantalla que
// tiene el selector. Adivinar cuál quiere y mandarlo a pagar es el peor error
// posible de esta pantalla.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card.js';
import { Button } from '../components/ui/Button.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Icon } from '../components/Icon.js';
import { useSubscription, useAvailablePlans, useCreateSubscriptionCheckout } from '../hooks/useSettings.js';

export function ActivarPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: sub, isLoading: subLoading } = useSubscription();
  const { data: plansData, isLoading: plansLoading } = useAvailablePlans();
  const checkout = useCreateSubscriptionCheckout();
  const [error, setError] = useState<string | null>(null);
  // El disparo es un efecto secundario que no puede repetirse: sin esto, un
  // re-render mientras la petición está en vuelo abre dos sesiones de checkout.
  const yaDisparado = useRef(false);

  const cargando = subLoading || plansLoading;
  const planes = plansData?.plans ?? [];

  useEffect(() => {
    if (cargando || yaDisparado.current || !sub) return;

    // Ya paga: no hay nada que activar. Se lo decimos en vez de mandarlo a
    // pagar otra vez.
    if (sub.status === 'active' || sub.billingExempt) return;

    // Precio de excepción cargado para esta cuenta → no hay que elegir plan.
    // Varios planes → que elija en la pantalla que tiene el selector.
    const conPrecio = planes.filter(p => p.amount != null);
    if (!sub.priceId && conPrecio.length !== 1) {
      navigate('/settings?tab=subscription', { replace: true });
      return;
    }

    yaDisparado.current = true;
    checkout.mutate(sub.priceId ? undefined : conPrecio[0]!.id, {
      onSuccess: r => { window.location.href = r.url; },
      onError: (err: unknown) => setError(err instanceof Error ? err.message : String(err)),
    });
  }, [cargando, sub, planes, checkout, navigate]);

  const yaPaga = sub != null && (sub.status === 'active' || sub.billingExempt);

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', padding: 'var(--evo-space-6)',
    }}>
      <Card>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)',
          alignItems: 'center', textAlign: 'center', maxWidth: '420px',
        }}>
          {error ? (
            <>
              <span style={{ color: 'var(--evo-danger)', display: 'inline-flex' }}>
                <Icon name="alert" size={24} />
              </span>
              <span style={{ fontSize: 'var(--evo-fs-h3)', color: 'var(--evo-fg-1)' }}>
                {t('activar.errorTitle', 'No pudimos abrir el pago')}
              </span>
              <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>{error}</span>
              <Button variant="solid" tone="cyan" size="sm" onClick={() => navigate('/settings?tab=subscription')}>
                {t('activar.goToSettings', 'Ir a la suscripción')}
              </Button>
            </>
          ) : yaPaga ? (
            <>
              <span style={{ color: 'var(--evo-success)', display: 'inline-flex' }}>
                <Icon name="check" size={24} />
              </span>
              <span style={{ fontSize: 'var(--evo-fs-h3)', color: 'var(--evo-fg-1)' }}>
                {t('activar.alreadyActive', 'Tu suscripción ya está activa')}
              </span>
              <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
                {t('activar.alreadyActiveHint', 'No hay nada que pagar. Puedes seguir usando tu cuenta con normalidad.')}
              </span>
              <Button variant="solid" tone="cyan" size="sm" onClick={() => navigate('/')}>
                {t('activar.goHome', 'Entrar a EvoHub')}
              </Button>
            </>
          ) : (
            <>
              <LoadingState size="sm" />
              <span style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)' }}>
                {t('activar.redirecting', 'Te estamos llevando al pago seguro de Stripe…')}
              </span>
            </>
          )}
        </div>
      </Card>
    </div>
  );
}
